import "./index.scss";

import React from 'react';
import ReactDOM from 'react-dom/client';
import { Routes, Route, Navigate, BrowserRouter as Router } from 'react-router-dom';
import * as Sentry from "@sentry/react";
import packageJson from '../package.json';

import RequireAuth from './components/requireAuth';
import RequireAuthAdmin from './components/requireAuthAdmin';
import { AuthProvider } from './components/authProvider';
import Page404 from './pages/page404';
import PageSignIn from './pages/pageSignIn';
import MainLayout from "./pages/mainLayout";
import ComLoading from "./components/comLoading";
import { showConsoleBanner } from "./helper/helperGeneral";

const AppDashboard = React.lazy(() => import('./pages/appDashboard'));
const AppMembers = React.lazy(() => import('./pages/appMembers'));
const AppExchange = React.lazy(() => import('./pages/appExchange'));
const AppEmailHistory = React.lazy(() => import('./pages/appEmailHistory'));
const AppMonitoring = React.lazy(() => import('./pages/appMonitoring'));
const AppBilling = React.lazy(() => import('./pages/appBilling'));
const AppProfile = React.lazy(() => import('./pages/appProfile'));
const AppDomains = React.lazy(() => import('./pages/appDomains'));
const AppEmailRules = React.lazy(() => import('./pages/appEmailRules'));
const AppEmailDelivery = React.lazy(() => import('./pages/appEmailDelivery'));
const AppSubscriptionSuccess = React.lazy(() => import('./pages/appSubscriptionSuccess'));
const AdminTools = React.lazy(() => import('./pages/adminTools'));
const AdminDashboard = React.lazy(() => import('./pages/adminDashboard'));

const isLocalhost = (window.location.hostname === 'localhost');
Sentry.init({
	dsn: (isLocalhost) ? "" : "https://340ffa7327af33184489a7b61a2b8e75@o4509367945068544.ingest.us.sentry.io/4509367971151872",
	sendDefaultPii: true,
	integrations: isLocalhost
		? []
		: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration(),
		],
	release: packageJson.version,
	environment: import.meta.env.NODE_ENV,
	tracesSampleRate: isLocalhost ? 0 : 1.0,
	attachStacktrace: true,
	replaysSessionSampleRate: isLocalhost ? 0 : 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: isLocalhost ? 0 : 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

showConsoleBanner(packageJson.version);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<Router future={{ v7_startTransition: true, v7_relativeSplatPath: true, }}>
	<Sentry.ErrorBoundary fallback={
		<div className="alert alert-danger d-flex align-items-center justify-content-center vh-100 text-center"
			style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999 }}>
			Please retry in a few seconds. If the issue persists, please immediately contact support at <a href="mailto:support@sparklingemail.com">support@sparklingemail.com</a>.
		</div>} showDialog>
		<AuthProvider>
			<Routes>
				{/* ==== Public Routes ==== */}
				<Route path="/login" element={<PageSignIn />} />
				<Route path="/sign-in" element={<Navigate to="/login" replace />} />
				<Route path="/sign-up" element={<Navigate to="/login" replace />} />
				<Route path="/sign-out" element={<Navigate to="/login" replace />} />
				<Route path="/signin" element={<Navigate to="/login" replace />} />
				<Route path="/signup" element={<Navigate to="/login" replace />} />
				<Route path="/signout" element={<Navigate to="/login" replace />} />

				{/* ==== Dashboard Routes ==== */}
				<Route path="/*" element={
					<RequireAuth>
						<React.Suspense fallback={<ComLoading message="Loading your dashboard..." />}>
							<MainLayout>
								<Routes>
									<Route path="/" element={<AppDashboard />} />
									<Route path="/profile" element={<AppProfile />} />
									<Route path="/domains" element={<AppDomains />} />
									<Route path="/members" element={<AppMembers />} />
									<Route path="/rules" element={<AppEmailRules />} />
									<Route path="/deliveries" element={<AppEmailDelivery />} />
									<Route path="/history" element={<AppEmailHistory />} />
									<Route path="/billing" element={<AppBilling />} />
									<Route path="/billing/success" element={<AppSubscriptionSuccess />} />
									<Route path="/exchange" element={<AppExchange />} />
									<Route path="/monitoring" element={<AppMonitoring />} />
									{/* ==== Super Admin Routes ==== */}
									<Route path="/admin/*" element={
										<RequireAuthAdmin>
											<Routes>
												<Route path="/" element={<Navigate to="/admin/tools" replace />} />
												<Route path="/tools" element={<AdminTools />} />
												<Route path="/dashboard" element={<AdminDashboard />} />
											</Routes>
										</RequireAuthAdmin>
									} />

									{/* <Route path="*" element={<Navigate to="/" replace />} /> */}
									<Route path="*" element={<Page404 />} />
								</Routes>
							</MainLayout>
						</React.Suspense>
					</RequireAuth>
				} />

				{/* ==== 404 Page ==== */}
				<Route path="*" element={<Page404 />} />
			</Routes>
		</AuthProvider>
	</Sentry.ErrorBoundary>
</Router>);